/**
 * `<ReportFrame>` — sandboxed iframe that renders the engine's HTML output.
 *
 * Each report's display template includes its own `<style>` block (Indian
 * accountant tables aren't shy about colour). Rendering inside a sandboxed
 * iframe isolates those styles from the host SPA, so a `body { font: ... }`
 * in one report can't bleed into the whole web app.
 *
 * Sizes the iframe to `body.scrollHeight` on every layout shift inside it
 * (initial load, refresh with identical HTML, font/image late-arrivals,
 * filter changes). A `ResizeObserver` on the inner body handles the "first
 * paint measured short, real height turned out taller" case that a bare
 * `onLoad` measurement misses.
 */

import { useEffect, useRef } from "react";

export interface ReportFrameProps {
  html: string;
  /**
   * Minimum height while content is loading — keeps the iframe from
   * collapsing to 0px during the first paint. Kept small so a 5-row
   * report doesn't leave a tall band of empty white space below the
   * table (was 600px, which floored short reports).
   */
  minHeight?: number;
}

export function ReportFrame({ html, minHeight = 120 }: ReportFrameProps) {
  const ref = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const iframe = ref.current;
    if (!iframe) return undefined;

    let observer: ResizeObserver | null = null;

    const resize = () => {
      const doc = iframe.contentDocument;
      const body = doc?.body;
      const view = doc?.defaultView;
      if (!body || !view) return;
      // body.scrollHeight alone underreports when the report template sets
      // `body { margin: N }` — those margins live outside body's own scroll
      // box but inside <html>, so the iframe ends up 2*N shorter than the
      // real render and an inner scrollbar appears. Add the computed margins
      // to get the true rendered height.
      // We deliberately don't use documentElement.scrollHeight because some
      // templates set `html { height: 100% }` and it then pads to viewport.
      const cs = view.getComputedStyle(body);
      const marginTop = parseFloat(cs.marginTop) || 0;
      const marginBottom = parseFloat(cs.marginBottom) || 0;
      const h = body.scrollHeight + marginTop + marginBottom;
      if (h > 0) {
        iframe.style.height = `${h}px`;
        iframe.style.minHeight = "0px";
      }
    };

    const attach = () => {
      const doc = iframe.contentDocument;
      if (!doc?.body) return;
      // First measurement synchronous with the load event. Then observe so
      // any post-load layout shift (fonts, images, iframe width changes)
      // keeps the outer height in sync.
      resize();
      observer?.disconnect();
      observer = new ResizeObserver(resize);
      observer.observe(doc.body);
    };

    iframe.addEventListener("load", attach);
    // If srcDoc has already loaded by the time this effect runs (e.g. same
    // HTML rerender), attach directly — the load event won't fire again.
    if (iframe.contentDocument?.readyState === "complete") attach();

    return () => {
      iframe.removeEventListener("load", attach);
      observer?.disconnect();
    };
  }, [html]);

  return (
    <iframe
      ref={ref}
      srcDoc={html}
      sandbox="allow-same-origin"
      title="Report content"
      className="w-full border-0 bg-transparent"
      style={{ minHeight: `${minHeight}px` }}
    />
  );
}
